<template>
    <Layout disable-vertical-padding disable-horizontal-padding>
        <div v-bind:class="$style.container">
            <VFlex v-bind:class="$style.box">
                <form>
                    <header v-bind:class="$style.header">
                        <h2>My Account Sign-Up</h2>
                        <p v-bind:class="$style.subheader">
                            Already have an account?
                            <AppLink name="login" v-bind:class="$style.bold">
                                Log in here
                            </AppLink>
                        </p>
                    </header>
                    <VDivider v-bind:class="$style.divider" />
                    <VAlert
                        type="error"
                        outline
                        v-bind:value="serverError"
                        v-bind:class="$style.alert"
                    >
                        {{ serverError }}
                    </VAlert>
                    <VTextField
                        v-model.trim="email"
                        type="email"
                        label="Email"
                        v-bind:error-messages="
                            serverErrors.email || TextFieldValidation_getErrorMessages('email')
                        "
                        required
                        v-on:blur="$v.email.$touch()"
                        v-on:keypress.enter="register"
                    />
                    <VTextField
                        v-model.trim="password"
                        v-bind:type="passwordInputType"
                        label="Password"
                        v-bind:error-messages="
                            serverErrors.password ||
                                TextFieldValidation_getErrorMessages('password', {
                                    messages: {
                                        hasLowercaseCharacter:
                                            'Password must contain at least one lower case letter.',
                                        hasUppercaseCharacter:
                                            'Password must contain at least one upper case letter.',
                                        hasSpecialCharacter:
                                            'Password must contain at least one number or symbol.',
                                    },
                                })
                        "
                        required
                        v-on:blur="$v.password.$touch()"
                        v-on:keypress.enter="register"
                    />
                    <VCheckbox v-model="isShowingPassword" label="Show password" hide-details />
                    <VBtn
                        color="primary"
                        depressed
                        large
                        block
                        v-bind:loading="isRegistering"
                        v-bind:class="$style.button"
                        v-on:click="register"
                    >
                        Create my account
                    </VBtn>
                </form>
            </VFlex>
        </div>
    </Layout>
</template>

<script>
    import Layout from '@layouts/main';
    import TextFieldValidation from '@mixins/TextFieldValidation';
    import _has from 'lodash/has';
    import {
        required, email, minLength,
    } from 'vuelidate/lib/validators';

    export default {
        /**
         * Custom page meta data property for the view components.
         *
         * @type {Object}
         */
        metaInfo: {
            title: 'Account Signup',
            meta: [
                {
                    name: 'description',
                    content: 'Signup for Citizen J.',
                },
            ],
            // This is a placeholder to ensure proper canonical is set from specified SEO url
            // Full canonical URL including top level domain should be used
            link: [
                {
                    rel: 'canonical',
                    href: '/signup',
                },
            ],
        },

        /**
         * Declaratively register required components.
         *
         * @link https://vuejs.org/v2/guide/components.html#Local-Registration
         */
        components: {
            Layout,
        },

        /**
         * Mixins are a flexible way to distribute reusable functionalities for Vue components.
         * A mixin object can contain any component options. When a component uses a mixin,
         * all options in the mixin will be “mixed” into the component’s own options.
         *
         * @link https://vuejs.org/v2/guide/mixins.html
         */
        mixins: [TextFieldValidation],

        /**
         * You must define defaults for all Vue models and reactive Vue template data.
         *
         * @link https://vuejs.org/v2/api/#data
         *
         * @return Object Vue model and binding defaults.
         */
        data () {
            return {
                email: '',
                password: '',
                serverErrors: {},
                serverError: null,
                isRegistering: false,
                isShowingPassword: false,
            };
        },

        /**
         * Cached computed methods for complex logic on the view layer.
         *
         * @link https://vuejs.org/v2/guide/computed.html
         *
         * @type Object The computed functions
         */
        computed: {
            passwordInputType () {
                return this.isShowingPassword ? 'text' : 'password';
            },
        },

        /**
         * Non-cached methods that are used by the Vue instance and template.
         *
         * @type Object ES6 methods
         */
        methods: {
            register () {
                // Reset server errors
                this.serverErrors = {};

                // Verify that the form is valid
                if (this.$v.$invalid) {
                    return this.$v.$touch();
                }

                // Set reactive state defaults
                this.isRegistering = true;

                const { email, password } = this;

                // Register the user via the vue-auth package
                this.$auth.register({
                    data: {
                        email,
                        password,
                    },
                    error: ({ response }) => {
                        this.isRegistering = false;

                        // Determine if any field server validation errors are set
                        if (_has(response, 'data.errors')) {
                            return (this.serverErrors = response.data.errors);
                        }

                        // Display the generic invalid credentials error
                        this.serverError = response.data.message;
                    },
                    rememberMe: true,
                    autoLogin: true,
                    redirect: '/',
                });
            },
        },

        /**
         * Define custom validation rules on reactive data for Vuelidate validation.
         *
         * @link https://monterail.github.io/vuelidate
         *
         * @type {Object}
         */
        validations: {
            email: {
                required,
                email,
            },
            password: {
                required,
                minLength: minLength(8),
                hasLowercaseCharacter: value => Boolean(value.match(/[a-z]/)),
                hasUppercaseCharacter: value => Boolean(value.match(/[A-Z]/)),
                hasSpecialCharacter: value => Boolean(value.match(/[^a-zA-Z]/)),
            },
        },
    };
</script>

<style lang="scss" module>
    @import '@design';

    .box {
        @apply px-10 pt-12 pb-8 bg-white rounded-lg;
    }

    .header {
        @apply text-center;
    }

    .subheader {
        @apply mt-4;
    }

    .divider {
        @apply my-6;
    }

    .container {
        // This needs to remain nested to overide Vuetify button margins
        .button {
            @apply my-6;
        }
    }

    .bold {
        @apply font-bold;
    }

    .alert {
        @apply mb-4;
    }

    @screen sm {
        .container {
            @apply px-6 py-12 flex items-center justify-center;

            // This needs to remain nested to overide Vuetify flex class max-width
            .box {
                max-width: 42rem;
            }
        }
    }
</style>
